import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { toast } from '../../lib/toast'
import { staggerProps } from '../../lib/stagger'
import { SeatMeter } from './SeatMeter'
import { InviteComposer } from './InviteComposer'
import { InviteRow, MemberRow } from './MemberRow'
import { JoinRequestQueue, type JoinRowBusy } from './JoinRequestQueue'
import { SeatPaywall } from './SeatPaywall'
import { OrgDialog } from '../org/OrgDialog'
import { BareState } from '../org/BareState'
import { canPurchaseSeats } from '../../lib/seatPlan'
import { catalogExit } from '../../lib/catalogExit'
import { useIdentity } from '../../lib/identity'
import {
  clearPendingInvite, readPendingInvite, stashPendingInvite,
} from '../../lib/inviteToken'
import {
  inviteAccess, memberAccess, OrgActionMessage, orgErrorCode, orgErrorMessage, orgKycStatusOf,
  seatLimitFrom, type OrgSeatsLite,
} from '../../lib/orgAccess'
import {
  orgApi, seatBillingApi, SeatPurchaseUnavailable,
  type OrgFull, type OrgInviteRow, type OrgJoinRequestRow, type OrgMemberRow,
  type SeatBillingState,
} from '../../api/endpoints'

/**
 * TeamPanel — the whole team surface, shell-agnostic. `routes/AccountTeam.tsx`
 * and `mobile/screens/account/TeamScreen.tsx` wrap it in their own chrome.
 *
 * ── THE PATH IS LOAD-BEARING ──────────────────────────────────────────────
 * `functions/src/billing/index.ts` hardcodes
 * `RETURN_BASE = 'https://www.thediaex.com/account/team'` with
 * `?seats=purchased|cancelled`. A Stripe return that 404s into the `path="*"`
 * redirect is a money-path dead end, so `/account/team` must exist on BOTH
 * shells and must be exactly that string.
 *
 * ── SEAT NUMBERS ARE NEVER ADJUSTED LOCALLY ───────────────────────────────
 * They are derived server-side and recounted inside every transaction. After
 * any mutation we refetch `GET /org` rather than decrementing a local count —
 * an optimistic number that disagrees with the recount is how a client sells a
 * seat that does not exist. Optimism is allowed only on a row's visual state.
 *
 * ── NOTHING HERE CALLS signOut() ──────────────────────────────────────────
 * Not once, in any state. That is the rule the 401 classifier broke (an
 * overlay whose only control signed the user out, so they signed back in and
 * hit the same wall) and it is a review gate on this surface.
 */

type Phase = 'loading' | 'ready' | 'no_org' | 'not_member' | 'suspended' | 'unavailable' | 'error'

type Busy = null | 'role' | 'remove' | 'revoke' | 'transfer' | 'resend'

export interface TeamPanelProps {
  /** Rendered by the shell wrapper; the panel itself never draws chrome. */
  onBack?: () => void
}

export function TeamPanel({ onBack }: TeamPanelProps) {
  const navigate = useNavigate()
  const identity = useIdentity()
  const [params, setParams] = useSearchParams()

  const [phase, setPhase] = useState<Phase>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [org, setOrg] = useState<OrgFull | null>(null)
  const [members, setMembers] = useState<OrgMemberRow[]>([])
  const [invites, setInvites] = useState<OrgInviteRow[]>([])
  const [billing, setBilling] = useState<SeatBillingState | null>(null)

  const [email, setEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)

  const [openRow, setOpenRow] = useState<string | null>(null)
  const [rowError, setRowError] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<Busy>(null)
  // A failed `GET /org/invites` used to be swallowed whole. See `loadInvites`.
  const [invitesUnavailable, setInvitesUnavailable] = useState(false)
  const [invitesRetrying, setInvitesRetrying] = useState(false)

  // The join queue — the OTHER way a seat gets consumed, and the half of the
  // employee-signup flow that had no screen at all.
  const [joinRequests, setJoinRequests] = useState<OrgJoinRequestRow[]>([])
  const [joinUnavailable, setJoinUnavailable] = useState(false)
  const [joinBusy, setJoinBusy] = useState<JoinRowBusy>(null)
  const [joinError, setJoinError] = useState<Record<string, string>>({})

  const [paywall, setPaywall] = useState<OrgSeatsLite | null>(null)
  const [paywallBusy, setPaywallBusy] = useState(false)
  const [paywallError, setPaywallError] = useState<string | null>(null)
  const [meterShared, setMeterShared] = useState(true)
  const [countAnim, setCountAnim] = useState(false)
  const [returnState, setReturnState] = useState<null | 'waiting' | 'stalled'>(null)
  const [dunningDismissed, setDunningDismissed] = useState(false)

  const meterRef = useRef<HTMLDivElement>(null)
  const rosterRef = useRef<HTMLUListElement>(null)
  // Where focus goes after a mutation that unmounted the control just pressed.
  // See `returnFocus` below — this is a testid, resolved after the commit that
  // contains the refetched roster.
  const [focusReturn, setFocusReturn] = useState<string | null>(null)

  const viewerRole = org?.viewerOrgRole ?? null
  const canManage = viewerRole === 'owner' || viewerRole === 'admin'
  const orgName = org?.displayName || org?.legalName || 'your organization'
  const seats = org?.seats ?? null
  const orgKyc = orgKycStatusOf(org)

  /* ── load ──────────────────────────────────────────────────────────── */

  /** Owner/admin-only reads, each independently best-effort: a failed invites
   *  read must not blank the roster, and a failed billing read must not hide
   *  the team. The join queue is the exception — a failed READ there is
   *  rendered, because "nobody has asked" and "we couldn't ask" are different
   *  answers and only one of them is safe to show as an empty list. */
  const loadJoinQueue = useCallback(async () => {
    try {
      const r = await orgApi.joinRequests()
      setJoinRequests(r.requests ?? [])
      setJoinUnavailable(r.queueUnavailable === true)
    } catch {
      // The server already distinguishes a broken query from an empty one; a
      // transport failure is the same class of thing and gets the same honest
      // banner rather than silently swallowing somebody's request.
      setJoinRequests([])
      setJoinUnavailable(true)
    }
  }, [])

  /**
   * The pending-invite list. It used to be `.catch(() => {})` — a silent
   * swallow — and that is not survivable here, because the SEAT METER above it
   * is drawn from `orgs/{id}.seats`, not from this list. So when the read
   * failed the owner saw a hollow gold pip and the words "1 held by an invite"
   * over a roster with no invite row in it: a seat asserted to be spent, on
   * somebody who does not appear, with no way to revoke it and nothing on
   * screen admitting anything had gone wrong. The invite is real and the
   * server is right; only our copy of the list is missing, and the banner says
   * exactly that and offers the retry.
   *
   * The roster is deliberately NOT blanked — a failed invites read must not
   * take the members with it.
   */
  const loadInvites = useCallback(async () => {
    try {
      const r = await orgApi.invites()
      setInvites(r.invites)
      setInvitesUnavailable(false)
    } catch {
      setInvitesUnavailable(true)
    }
  }, [])

  const retryInvites = useCallback(async () => {
    setInvitesRetrying(true)
    try { await loadInvites() } finally { setInvitesRetrying(false) }
  }, [loadInvites])

  const loadManagerReads = useCallback(() => {
    void loadInvites()
    void seatBillingApi.get().then(setBilling).catch(() => { /* price falls back to SEAT_PLAN */ })
    void loadJoinQueue()
  }, [loadInvites, loadJoinQueue])

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) { setPhase('loading'); setLoadError(null) }
    try {
      // ── THE ORG ANSWER DECIDES, AND IT IS ASKED FOR ITS OWN SAKE ────────
      // `GET /org/members` admits ACTIVE members only, so a SUSPENDED seat
      // gets a 403 `not_org_member` from it. Awaiting both together therefore
      // used to throw that 403 and paint "You're no longer part of that
      // organization" over a person who still works there — and the suspended
      // branch below was unreachable. `allSettled` keeps the round trips
      // parallel while letting `GET /org` (which deliberately admits a
      // suspended member with a reduced view) be the one that decides.
      const [orgSettled, memberSettled] = await Promise.allSettled([orgApi.get(), orgApi.members()])
      if (orgSettled.status === 'rejected') throw orgSettled.reason
      const orgRes = orgSettled.value
      setOrg(orgRes.org)
      if (orgRes.org.restricted || orgRes.seatSuspended) {
        setMembers([]); setInvites([]); setBilling(null)
        setJoinRequests([]); setJoinUnavailable(false); setInvitesUnavailable(false)
        setPhase('suspended'); return
      }
      if (memberSettled.status === 'rejected') throw memberSettled.reason
      setMembers(memberSettled.value.members)
      setPhase('ready')
      const role = orgRes.org.viewerOrgRole
      if (role === 'owner' || role === 'admin') {
        loadManagerReads()
      } else {
        setInvites([]); setBilling(null); setJoinRequests([]); setJoinUnavailable(false)
        setInvitesUnavailable(false)
      }
    } catch (err) {
      const code = orgErrorCode(err)
      if (code === 'no_org') { setPhase('no_org'); return }
      if (code === 'not_org_member') { setPhase('not_member'); return }
      // A 404 with no code is `ORGS_LIVE` off — the router 404s before
      // requireAuth so an unarmed deploy is indistinguishable from a build
      // with no org code at all. That is "not available here", never an error.
      if (code === 'not_found') { setPhase('unavailable'); return }
      setLoadError(orgErrorMessage(err, 'Couldn’t load your team.'))
      setPhase('error')
    }
  }, [loadManagerReads])

  // Not until there is a token. `/org` sits behind `requireAuth`, so firing it
  // anonymously buys one guaranteed 401 and an error state for a person who is
  // simply signed out.
  useEffect(() => {
    if (identity.status === 'loading') return
    if (!identity.signedIn) return
    void load()
  }, [load, identity.status, identity.signedIn])

  /** Refetch after a mutation. Never adjust a seat number by hand. */
  const refetch = useCallback(async () => {
    try {
      const [orgRes, memberRes] = await Promise.all([orgApi.get(), orgApi.members()])
      setOrg(orgRes.org)
      setMembers(memberRes.members)
      const role = orgRes.org.viewerOrgRole
      if (role === 'owner' || role === 'admin') {
        void loadInvites()
        void loadJoinQueue()
      }
    } catch { /* the row-level error already said what failed */ }
  }, [loadInvites, loadJoinQueue])

  /* ── the meter's shared-element guard ──────────────────────────────── */

  // A `layoutId` whose source is scrolled out of view animates from nowhere
  // and reads as broken. Measure at open time; if the header meter is not on
  // screen, drop the shared id and let the sheet hero crossfade instead.
  const openPaywall = useCallback((s: OrgSeatsLite) => {
    const box = meterRef.current?.getBoundingClientRect()
    const visible = !!box && box.bottom > 0 && box.top < window.innerHeight
    setMeterShared(visible)
    setPaywallError(null)
    setPaywall(s)
  }, [])

  const seatsLite = useCallback((): OrgSeatsLite => ({
    used: seats?.used ?? 0,
    included: seats?.included ?? 0,
    paid: seats?.paid ?? 0,
    limit: seats?.limit ?? 0,
    pendingInvites: seats?.pendingInvites ?? 0,
  }), [seats])

  /* ── invite ────────────────────────────────────────────────────────── */

  const sendInvite = useCallback(async (
    addr: string,
    role: 'admin' | 'member',
  ): Promise<'sent' | 'paywall' | 'failed'> => {
    setInviting(true)
    setInviteError(null)
    try {
      const res = await orgApi.createInvite(addr.trim(), role)
      feedback.success()
      // The meter's count-up runs only for a change the user just caused —
      // never on mount, never on a re-render (a dashboard that counts up on
      // every paint is the most overused trick in the genre).
      setCountAnim(true)
      setEmail('')
      await refetch()
      toast(
        res.emailed?.redirected
          ? `Invite created — the email went to our ops inbox while sending is off.`
          : `Invite sent to ${addr.trim()}`,
        'success',
      )
      return 'sent'
    } catch (err) {
      // 402 IS THE PAYWALL, 409 IS A CONFLICT. Collapsing them makes "buy a
      // seat" indistinguishable from "reload the page".
      const limit = seatLimitFrom(err)
      if (limit) {
        feedback.dim()
        openPaywall(limit)
        return 'paywall'
      }
      const code = orgErrorCode(err)
      if (code === 'already_a_member') {
        setInviteError(`${addr.trim()} is already on your team.`)
      } else if (code === 'invite_already_pending') {
        setInviteError(`${addr.trim()} already has an invite waiting — you can resend or revoke it below.`)
      } else {
        setInviteError(orgErrorMessage(err, 'Couldn’t send that invite. Try again.'))
      }
      feedback.deny()
      return 'failed'
    } finally {
      setInviting(false)
    }
  }, [openPaywall, refetch])

  /* ── the join queue ────────────────────────────────────────────────── */

  const setJoinRowError = useCallback((uid: string, msg: string | null) => {
    setJoinError((m) => {
      const next = { ...m }
      if (msg) next[uid] = msg; else delete next[uid]
      return next
    })
  }, [])

  /**
   * Approve — the same seat purchase an invite is, from the other side. A 402
   * therefore opens the SAME paywall the 4th invite does, and the request is
   * left PENDING server-side so that buying a seat and pressing Approve again
   * works. A seat wall that destroyed the request would make the paywall the
   * most expensive button on the screen.
   */
  const approveJoin = useCallback(async (uid: string) => {
    setJoinBusy({ uid, kind: 'approve' })
    setJoinRowError(uid, null)
    try {
      await orgApi.approveJoinRequest(uid)
      feedback.success()
      setCountAnim(true)
      await refetch()
      toast('They’re on the team.', 'success')
    } catch (err) {
      const limit = seatLimitFrom(err)
      if (limit) {
        feedback.dim()
        // Say it in the row too: the sheet can be dismissed, and an owner who
        // dismisses it must still know why nothing happened.
        setJoinRowError(uid, 'You’re out of seats — add one and approve again. Their request is still waiting.')
        openPaywall(limit)
        return
      }
      feedback.deny()
      setJoinRowError(uid, orgErrorMessage(err, 'We couldn’t approve that request.'))
      // A refusal usually means the row moved underneath us (they joined
      // elsewhere, it expired, someone else answered it) — and the server
      // SPENDS it in most of those cases, so re-reading is what clears it.
      void loadJoinQueue()
    } finally {
      setJoinBusy(null)
    }
  }, [loadJoinQueue, openPaywall, refetch, setJoinRowError])

  const declineJoin = useCallback(async (uid: string, reason: string) => {
    setJoinBusy({ uid, kind: 'decline' })
    setJoinRowError(uid, null)
    try {
      await orgApi.declineJoinRequest(uid, reason)
      feedback.dim()
      await loadJoinQueue()
      toast('Declined. They keep their own account.', 'success')
    } catch (err) {
      feedback.deny()
      setJoinRowError(uid, orgErrorMessage(err, 'We couldn’t decline that request.'))
      void loadJoinQueue()
    } finally {
      setJoinBusy(null)
    }
  }, [loadJoinQueue, setJoinRowError])

  /* ── the Stripe return ─────────────────────────────────────────────── */

  const consumeStripeReturn = useCallback(async (orgId: string) => {
    const held = readPendingInvite(orgId)
    setReturnState('waiting')
    // The webhook that grows `seats.limit` is asynchronous. Poll — and if it
    // has not landed, say so honestly rather than firing an invite that will
    // 402 again or claiming a success we cannot see.
    const delays = [0, 1500, 2500]
    for (const wait of delays) {
      if (wait) await new Promise((r) => setTimeout(r, wait))
      try {
        const res = await orgApi.get()
        setOrg(res.org)
        const grew = (res.org.seats?.limit ?? 0) > (res.org.seats?.used ?? 0) + (res.org.seats?.pendingInvites ?? 0)
        if (grew) {
          setCountAnim(true)
          feedback.success()
          if (held) {
            const outcome = await sendInvite(held.email, held.orgRole)
            // Only drop the stash once the invite actually exists — a crash
            // mid-flow must not cost the owner the address they typed.
            if (outcome === 'sent') clearPendingInvite(orgId)
            else setEmail(held.email)
          }
          setReturnState(null)
          await refetch()
          return
        }
      } catch { /* fall through to the honest stalled state */ }
    }
    if (held) setEmail(held.email)
    setReturnState('stalled')
  }, [refetch, sendInvite])

  const seatsParam = params.get('seats')
  // A one-shot latch. The effect legitimately depends on `params`/`setParams`,
  // whose identity changes every render, and re-entering it would either fire
  // the held invite twice or (worse) re-strip a param that is already gone and
  // leave a paying customer on a screen that says nothing happened.
  const stripeReturnHandled = useRef<string | null>(null)
  useEffect(() => {
    if (!seatsParam || !org?.id) return
    const key = `${org.id}:${seatsParam}`
    if (stripeReturnHandled.current === key) return
    stripeReturnHandled.current = key
    // Strip the query param first so a refresh doesn't re-run the flow.
    const next = new URLSearchParams(params)
    next.delete('seats')
    setParams(next, { replace: true })
    if (seatsParam === 'purchased') {
      void consumeStripeReturn(org.id)
    } else if (seatsParam === 'cancelled') {
      // Cancelling a purchase is NOT a failure and is not styled like one.
      const held = readPendingInvite(org.id)
      if (held) setEmail(held.email)
      feedback.dim()
    }
  }, [seatsParam, org?.id, consumeStripeReturn, params, setParams])

  /* ── purchase ──────────────────────────────────────────────────────── */

  async function buySeats(total: number) {
    if (!org?.id) return
    setPaywallBusy(true)
    setPaywallError(null)
    // Stash BEFORE leaving: the whole point is that the owner never retypes
    // the address, and we are about to hand the tab to Stripe.
    if (email.trim()) {
      stashPendingInvite({ orgId: org.id, email: email.trim(), orgRole: inviteRole })
    }
    try {
      const res = await seatBillingApi.checkoutSession(total)
      if (res.url) { window.location.assign(res.url); return }
      // `{configured:false}` is a 200. Render the server's own message; never
      // leave a spinner that resolves to nothing.
      setPaywallError(res.message ?? 'Seat purchases aren’t switched on yet.')
    } catch (err) {
      if (err instanceof SeatPurchaseUnavailable) {
        setPaywallError('Seats are managed on the web. Open thediaex.com/account/team on a computer.')
      } else {
        setPaywallError(orgErrorMessage(err, 'Couldn’t open checkout. Try again.'))
      }
    } finally {
      setPaywallBusy(false)
    }
  }

  async function openPortal() {
    try {
      const res = await seatBillingApi.portal()
      if (res.url) { window.location.assign(res.url); return }
      toast(res.message ?? 'Billing isn’t switched on yet.', 'error')
    } catch (err) {
      if (err instanceof SeatPurchaseUnavailable) {
        toast('Billing is managed on the web.', 'error')
      } else {
        toast(orgErrorMessage(err, 'Couldn’t open billing.'), 'error')
      }
    }
  }

  /* ── row mutations ─────────────────────────────────────────────────── */

  function setError(key: string, msg: string | null) {
    setRowError((m) => {
      const next = { ...m }
      if (msg) next[key] = msg; else delete next[key]
      return next
    })
  }

  /**
   * ── FOCUS AFTER A MUTATION ────────────────────────────────────────────────
   * Every destructive action here is pressed inside the row it destroys. When
   * the refetch lands, that button is gone, and the browser's answer to
   * "focus was on a node that no longer exists" is `<body>` — so a keyboard
   * user who removed the third of twelve teammates was returned to the top of
   * the document and had to tab back down, and a screen-reader user was told
   * nothing at all about where they now were. (The toast is a live region and
   * says WHAT happened; it cannot say where you are.)
   *
   * So: aim for the row's own header, which is the thing that was open. If it
   * survived (a role change, a completed transfer) focus lands exactly where
   * the user was; if it did not (remove, revoke, resend — which mints a new
   * invite id) we fall back to the roster itself, one Tab from the next row.
   */
  useEffect(() => {
    if (!focusReturn) return
    setFocusReturn(null)
    const el = document.querySelector<HTMLElement>(`[data-testid="${focusReturn}"]`)
    if (el?.isConnected) { el.focus(); return }
    rosterRef.current?.focus()
  }, [focusReturn])

  async function runRow(
    key: string,
    kind: Busy,
    fn: () => Promise<void>,
    okMsg?: string,
    /** The testid to return focus to once the mutation has settled. */
    returnFocusTo?: string,
  ) {
    setBusy(kind)
    setError(key, null)
    try {
      await fn()
      setCountAnim(true)
      await refetch()
      if (okMsg) toast(okMsg, 'success')
      if (returnFocusTo) setFocusReturn(returnFocusTo)
    } catch (err) {
      feedback.deny()
      setError(key, orgErrorMessage(err, 'That didn’t work. Try again.'))
    } finally {
      setBusy(null)
    }
  }

  /* ── derived ───────────────────────────────────────────────────────── */

  const now = Date.now()
  const activeMembers = members.filter((m) => m.status === 'active')
  const pausedMembers = members.filter((m) => m.status === 'suspended')
  const livingInvites = invites.filter((i) => i.status === 'pending' || i.status === 'expired')
  const subStatus = billing?.subscription?.status ?? org?.billing?.subscriptionStatus ?? 'none'
  // ── THE CATALOG EXIT KNOWS WHICH SHELL IT IS IN ──────────────────────
  // This panel is rendered verbatim by `routes/AccountTeam.tsx` and
  // `mobile/screens/account/TeamScreen.tsx`, and `/search` exists on the
  // DESKTOP tree only — on the app shell it hit `path="*"` and landed on Home
  // (or /welcome, signed out) under a button that said "Browse the catalog".
  const catalog = catalogExit(identity.signedIn)
  const browseAction = { label: catalog.label, onClick: () => navigate(catalog.path) }

  /* ── the states ────────────────────────────────────────────────────── */

  // The DESKTOP shell has no auth gate at all (a known, documented gap), so an
  // anonymous visitor who types this URL would otherwise get "Couldn't load
  // your team" over a 401 — an error screen for something that is not an error.
  if (identity.status === 'anon' || identity.status === 'unavailable') {
    return (
      <BareState
        title="Team"
        heading="Sign in to see your team."
        body="Your colleagues, their access and your seats live behind your account."
        primary={{ label: 'Sign in', onClick: () => navigate('/signin') }}
        secondary={browseAction}
        testId="team-signed-out"
      />
    )
  }

  if (phase === 'loading') {
    return (
      <div className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6" data-testid="team-panel">
        <h1 className="font-serif text-2xl tracking-h1 text-ink md:text-4xl">Team</h1>
        {/* Skeletons at the REAL heights, so nothing shifts on arrival. */}
        <div className="skeleton mt-5 h-1.5 w-full rounded-full" />
        <div className="skeleton mt-3 h-4 w-40 rounded" />
        <div className="rule-strong mt-5" />
        <div className="mt-4 space-y-2">
          <div className="skeleton h-16 rounded-md" />
          <div className="skeleton h-16 rounded-md" />
          <div className="skeleton h-16 rounded-md" />
        </div>
        <span className="sr-only" role="status">Loading your team</span>
      </div>
    )
  }

  if (phase === 'unavailable') {
    return (
      <BareState
        title="Team"
        heading="Teams aren’t switched on for this account yet."
        body="When they are, you’ll be able to invite colleagues here and they’ll inherit your company’s verification."
        primary={browseAction}
        secondary={onBack ? { label: 'Back', onClick: onBack } : undefined}
        testId="team-unavailable"
      />
    )
  }

  if (phase === 'no_org') {
    return (
      <BareState
        title="Team"
        heading="You’re not part of an organization yet."
        body="Companies on Diaex share one verification — everyone on the team sees prices without a review of their own."
        primary={{ label: 'Set up verification', onClick: () => navigate('/kyc') }}
        secondary={browseAction}
        testId="team-no-org"
      />
    )
  }

  if (phase === 'not_member') {
    return (
      <BareState
        title="Team"
        heading="You’re no longer part of that organization."
        body="Your account still works. Prices stay locked until you join a team again or are verified on your own."
        primary={browseAction}
        secondary={{ label: 'Contact support', href: `mailto:${SUPPORT}` }}
        testId="team-not-member"
      />
    )
  }

  if (phase === 'error') {
    return (
      <BareState
        title="Team"
        heading="Couldn’t load your team."
        body={loadError ?? 'Something went wrong on our side.'}
        primary={{ label: 'Try again', onClick: () => void load() }}
        secondary={onBack ? { label: 'Back', onClick: onBack } : browseAction}
        testId="team-load-error"
      />
    )
  }

  if (phase === 'suspended') {
    // The reduced projection genuinely carries no roster, no seats and no
    // billing — so we render none of them, rather than painting zeros.
    //
    // ── AND IT CARRIES NO OWNER EITHER, SO WE DO NOT CLAIM TO REACH ONE ──
    // This button used to say "Email {orgName}'s owner" over a mailto to Diaex
    // support. `view.ts::orgViewSuspended` deliberately withholds `ownerUid`
    // (and there is no roster on this projection), so we have no address for
    // them and never did. The label now names who actually receives it; the
    // body still says who can flip the seat back on, which is the fact the
    // reader needs.
    return (
      <BareState
        title={orgName}
        heading="Your seat is paused."
        body={`You can keep browsing — prices are hidden until an owner or admin at ${orgName} switches your seat back on. Ask them directly if you can; we can look into it too.`}
        primary={{
          label: 'Email Diaex support',
          href: `mailto:${SUPPORT}?subject=${encodeURIComponent(`Diaex — my seat at ${orgName} is paused`)}`,
        }}
        secondary={browseAction}
        testId="team-suspended"
      />
    )
  }

  /* ── ready ─────────────────────────────────────────────────────────── */

  const isEmpty = members.length <= 1 && livingInvites.length === 0

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6" data-testid="team-panel">
      <h1 className="font-serif text-2xl tracking-h1 text-ink md:text-4xl">Team</h1>
      <p className="mt-1 text-sm text-soft">{orgName}</p>

      <div ref={meterRef} className="mt-4">
        <SeatMeter
          used={seats?.used ?? activeMembers.length}
          pendingInvites={seats?.pendingInvites ?? livingInvites.length}
          paused={pausedMembers.length}
          limit={seats?.limit ?? 0}
          orgKyc={orgKyc}
          orgName={orgName}
          activeCount={activeMembers.length}
          animateCount={countAnim}
          onAddSeat={canManage ? () => openPaywall(seatsLite()) : undefined}
        />
      </div>

      <div className="rule-strong mt-5" />

      {/* ── dunning ────────────────────────────────────────────────── */}
      {canManage && subStatus === 'past_due' && !dunningDismissed && (
        <div className="mt-4 rounded-md border border-warning/40 bg-warning/[0.07] p-3" data-testid="team-past-due">
          <p className="text-[13px] font-semibold text-ink">Your card was declined.</p>
          <p className="mt-1 text-[13px] leading-relaxed text-soft">
            Everyone keeps full access
            {billing?.subscription?.graceEndsAt ? ` until ${formatDay(billing.subscription.graceEndsAt)}` : ' for now'}.
            Update your payment method and nothing changes.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {billing?.subscription?.manageable && canPurchaseSeats() && (
              <PressScale feel="tap" onClick={openPortal} className="btn-secondary h-11 px-4 text-[13px]" data-testid="team-update-payment">
                Update payment
              </PressScale>
            )}
            <PressScale feel="dim" onClick={() => setDunningDismissed(true)} className="btn-ghost h-11 px-4 text-[13px]">
              Dismiss
            </PressScale>
          </div>
        </div>
      )}
      {canManage && subStatus === 'canceled' && (
        <div className="mt-4 rounded-md border border-danger/35 bg-danger/[0.05] p-3" data-testid="team-canceled">
          <p className="text-[13px] font-semibold text-danger">
            Seats beyond your {seats?.included ?? 3} free ones are paused.
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-soft">
            {pausedMembers.length > 0
              ? `${pausedMembers.map((m) => m.displayName || m.email || 'a teammate').join(', ')} can still sign in and browse — with prices locked. Nobody was removed.`
              : 'Nobody was removed — a paused seat browses with prices locked.'}
          </p>
          {billing?.subscription?.manageable && canPurchaseSeats() && (
            <PressScale feel="tap" onClick={openPortal} className="btn-secondary mt-2.5 h-11 px-4 text-[13px]" data-testid="team-restore-seats">
              Restore seats
            </PressScale>
          )}
        </div>
      )}

      {/* ── the post-Stripe truth ──────────────────────────────────── */}
      {returnState === 'waiting' && (
        <p className="mt-4 rounded-md border border-line bg-cream p-3 text-[13px] text-ink" aria-live="polite" data-testid="team-seats-waiting">
          Confirming your seat with Stripe…
        </p>
      )}
      {returnState === 'stalled' && (
        <div className="mt-4 rounded-md border border-line bg-cream p-3" aria-live="polite" data-testid="team-seats-stalled">
          <p className="text-[13px] leading-relaxed text-ink">
            Your payment went through — we’re waiting on Stripe to confirm the seat. This usually
            takes a few seconds.
          </p>
          <PressScale
            feel="tap"
            onClick={() => { if (org?.id) void consumeStripeReturn(org.id) }}
            className="btn-secondary mt-2.5 h-11 px-4 text-[13px]"
            data-testid="team-seats-retry"
          >
            Try again
          </PressScale>
        </div>
      )}

      {/* ── people asking to get in ─────────────────────────────────
          Above the composer on purpose: somebody who has already asked is a
          higher-intent, time-limited decision than an address you have not
          typed yet, and the seat they consume is the same seat. Owner/admin
          only — `GET /org/join-requests` 403s for a plain member. */}
      {canManage && (
        <JoinRequestQueue
          requests={joinRequests}
          unavailable={joinUnavailable}
          busy={joinBusy}
          errors={joinError}
          onApprove={(uid) => void approveJoin(uid)}
          onDecline={(uid, reason) => void declineJoin(uid, reason)}
          onRetry={() => void loadJoinQueue()}
        />
      )}

      {canManage && (
        <div>
          <InviteComposer
            value={email}
            onChange={setEmail}
            role={inviteRole}
            onRoleChange={setInviteRole}
            canInviteAdmin={viewerRole === 'owner'}
            busy={inviting}
            error={inviteError}
            autoFocus={isEmpty}
            onSubmit={() => { void sendInvite(email, inviteRole) }}
          />
        </div>
      )}

      {isEmpty && (
        <p className="mt-4 text-sm leading-relaxed text-ink" data-testid="team-empty">
          You’re the only one here.{' '}
          {Math.max(0, (seats?.limit ?? 0) - 1) > 0
            ? `${Math.max(0, (seats?.limit ?? 0) - 1)} more ${Math.max(0, (seats?.limit ?? 0) - 1) === 1 ? 'seat is' : 'seats are'} free.`
            : ''}
        </p>
      )}

      {/* The invite list failed to load, and the meter is still counting the
          seats those invites hold. Say so, rather than letting the numbers and
          the roster quietly contradict each other. */}
      {canManage && invitesUnavailable && (
        <div
          className="mt-4 rounded-md border border-warning/40 bg-warning/[0.07] p-3"
          data-testid="team-invites-unavailable"
        >
          <p className="text-[13px] font-semibold text-ink">We couldn’t load your pending invites.</p>
          <p className="mt-1 text-[13px] leading-relaxed text-soft">
            {(seats?.pendingInvites ?? 0) > 0
              ? `Your seat count still includes ${seats?.pendingInvites === 1 ? 'an invite that is' : `${seats?.pendingInvites} invites that are`} holding ${seats?.pendingInvites === 1 ? 'a seat' : 'seats'} — they’re there, we just can’t list them right now, so you can’t revoke one until this loads.`
              : 'Anyone you’ve invited is still invited — we just can’t list them right now.'}
          </p>
          <PressScale
            feel="tap"
            disabled={invitesRetrying}
            onClick={() => { void retryInvites() }}
            className="btn-secondary mt-2.5 h-11 px-4 text-[13px]"
            data-testid="team-invites-retry"
          >
            {invitesRetrying ? 'Loading…' : 'Try again'}
          </PressScale>
        </div>
      )}

      <ul
        ref={rosterRef}
        role="list"
        // The landing pad for focus after a row deletes itself; see `runRow`.
        tabIndex={-1}
        aria-label="Team members"
        className="mt-5 divide-y divide-line focus:outline-none"
        data-testid="team-roster"
      >
        {members.map((m, i) => (
          <MemberRow
            key={m.uid}
            {...staggerProps(i)}
            member={m}
            access={memberAccess(m, orgKyc)}
            viewerRole={viewerRole}
            viewerUid={identity.uid}
            orgName={orgName}
            open={openRow === m.uid}
            onToggle={() => setOpenRow((k) => (k === m.uid ? null : m.uid))}
            error={rowError[m.uid] ?? null}
            busy={busy}
            onSetRole={(r) => void runRow(m.uid, 'role', async () => { await orgApi.setMemberRole(m.uid, r) })}
            onRemove={() => void runRow(m.uid, 'remove', async () => {
              await orgApi.removeMember(m.uid)
              feedback.dim()
              // Deliberately NOT "access revoked": `shared/identity.ts` memoizes
              // for 60s, so their entitlement can outlive this row by a minute.
            }, 'Removed from the team.', `team-member-${m.uid}`)}
            onLeave={() => void runRow(m.uid, 'remove', async () => {
              await orgApi.removeMember(m.uid)
              feedback.dim()
              navigate(catalog.path)
            })}
            onTransfer={() => void runRow(m.uid, 'transfer', async () => {
              await orgApi.transferOwnership(m.uid)
            }, 'Ownership transferred.', `team-member-${m.uid}`)}
          />
        ))}
        {livingInvites.map((inv, i) => (
          <InviteRow
            key={inv.inviteId}
            {...staggerProps(members.length + i)}
            invite={inv}
            access={inviteAccess(inv, now)}
            canManage={canManage}
            open={openRow === inv.inviteId}
            onToggle={() => setOpenRow((k) => (k === inv.inviteId ? null : inv.inviteId))}
            error={rowError[inv.inviteId] ?? null}
            busy={busy}
            onRevoke={() => void runRow(inv.inviteId, 'revoke', async () => {
              await orgApi.revokeInvite(inv.inviteId)
              feedback.dim()
            }, 'Invite revoked.', `team-invite-${inv.inviteId}`)}
            onResend={() => void runRow(inv.inviteId, 'resend', async () => {
              // There is no resend endpoint: an invite's secret is hashed at
              // rest, so the only copy is in the email we already sent. Revoke
              // (which returns the reserved seat) then mint a fresh one — same
              // address, same role.
              await orgApi.revokeInvite(inv.inviteId)
              if (!inv.email) return
              try {
                await orgApi.createInvite(inv.email, inv.orgRole === 'admin' ? 'admin' : 'member')
              } catch (err) {
                // THE HONEST FAILURE. The old invite is already gone, and
                // pretending otherwise would leave an owner watching a row
                // that no longer exists. Say what happened and leave the
                // address in the composer so one tap fixes it.
                setEmail(inv.email)
                setInviteRole(inv.orgRole === 'admin' ? 'admin' : 'member')
                throw new OrgActionMessage(
                  `${orgErrorMessage(err, 'We couldn’t send the new invite.')} The old one was cancelled first, so ${inv.email} now has none — the address is in the box above, ready to send again.`,
                )
              }
            }, 'A fresh invite is on its way.', `team-invite-${inv.inviteId}`)}
          />
        ))}
      </ul>

      {/* The member's own way out — never for the owner, who would strand the
          org. Their exit is Transfer ownership, on somebody else's row. */}
      {viewerRole === 'member' && (
        <p className="mt-6 text-[12.5px] text-soft">
          Open your own row above to leave {orgName}.
        </p>
      )}

      <OrgDialog
        open={!!paywall}
        onClose={() => setPaywall(null)}
        title={`Add your ${((paywall?.limit ?? 0) + 1)}${ordinal((paywall?.limit ?? 0) + 1)} teammate`}
        testId="seat-paywall-dialog"
      >
        {paywall && (
          <SeatPaywall
            seats={paywall}
            orgName={orgName}
            orgKyc={orgKyc}
            inviteEmail={email.trim()}
            viewerOrgRole={viewerRole}
            billingCountryCode={org?.billingCountryCode ?? null}
            configured={billing ? billing.configured : null}
            serverMessage={billing?.message ?? null}
            ownerEmail={members.find((m) => m.orgRole === 'owner')?.email ?? null}
            busy={paywallBusy}
            error={paywallError}
            sharedMeter={meterShared}
            onBuy={(total) => void buySeats(total)}
            onDismiss={() => setPaywall(null)}
          />
        )}
      </OrgDialog>
      {/* The shared-element guard's OFF state is observable, so the test can
          assert the degradation rather than trusting the comment. */}
      <span hidden data-testid="seat-meter-shared">{String(meterShared)}</span>
    </div>
  )
}

const SUPPORT = 'info@thediaex.com'

function ordinal(n: number): string {
  const rem100 = n % 100
  if (rem100 >= 11 && rem100 <= 13) return 'th'
  switch (n % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

function formatDay(iso: string): string {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return 'the end of your grace period'
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
